/**
 * 产品 KPI 看板（旧 views/analytics/LearningAnalytics.vue）：每个指标一张卡片，含当前值、定义、30 天与 90 天折线
 */
import { useMemo } from 'react'
import { Alert, Button, Card, Empty, Spin } from 'antd'
import { InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import type { DashboardSeriesResponse } from '@/api/metrics'
import { PageHeader } from '@/components/PageHeader'
import { EChart } from '@/components/charts/EChart'
import { PRODUCT_KPI_KEY, useKpiMetrics, useKpiSeries, type KpiMetric } from './hooks'
import { buildKpiChartOption, currentValueOf, formatMetricValue, metricLabel } from './kpi'
import styles from './ProductKpiPage.module.css'

const CHART_HEIGHT = 200

function errorText(error: unknown) {
  return `加载失败：${error instanceof Error ? error.message : String(error)}`
}

function SeriesChart({ title, series }: Readonly<{ title: string; series: DashboardSeriesResponse | null | undefined }>) {
  const option = useMemo(() => (series && series.series.length > 0 ? buildKpiChartOption(series) : null), [series])
  return (
    <div className={styles.chartBlock}>
      <div className={styles.chartTitle}>{title}</div>
      {option ? (
        <EChart option={option} height={CHART_HEIGHT} ariaLabel={title} />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />
      )}
    </div>
  )
}

function KpiCard({ metric }: Readonly<{ metric: KpiMetric }>) {
  const range30 = useKpiSeries(metric.name, '30d')
  const range90 = useKpiSeries(metric.name, '90d')
  const error = range30.error ?? range90.error
  return (
    <Card
      loading={range30.isPending || range90.isPending}
      title={
        <div className={styles.cardTitle}>
          <span className={styles.metricName}>{metricLabel(metric.name)}</span>
          <span className={styles.metricCurrent}>
            当前：{formatMetricValue(metric.name, currentValueOf(range30.data))}
          </span>
        </div>
      }
    >
      {error && <Alert type="error" title={errorText(error)} showIcon className={styles.alert} />}
      <p className={styles.definition}>
        <InfoCircleOutlined className={styles.definitionIcon} />
        {metric.definition}
      </p>
      <div className={styles.chartsRow}>
        <SeriesChart title="最近 30 天" series={range30.data} />
        <SeriesChart title="最近 90 天" series={range90.data} />
      </div>
    </Card>
  )
}

function KpiContent() {
  const metrics = useKpiMetrics()
  if (metrics.isPending) return <Spin className={styles.loading} />
  if (metrics.isError) return <Alert type="error" title="加载产品 KPI 列表失败" showIcon />
  if (metrics.data.length === 0) return <Empty description="后端尚未返回任何指标" />
  return (
    <div className={styles.grid}>
      {metrics.data.map((metric) => (
        <KpiCard key={metric.name} metric={metric} />
      ))}
    </div>
  )
}

export function ProductKpiPage() {
  const queryClient = useQueryClient()
  const fetchingCount = useIsFetching({ queryKey: PRODUCT_KPI_KEY })
  return (
    <div>
      <PageHeader
        title="产品 KPI 看板"
        subtitle="W4 留存 · M3 留存 · i+1 命中率 · 每日熟悉度净变化（东八区自然日，T+1 数据）"
        extra={
          <Button
            icon={<ReloadOutlined />}
            loading={fetchingCount > 0}
            onClick={() => queryClient.refetchQueries({ queryKey: PRODUCT_KPI_KEY, type: 'active' })}
          >
            刷新
          </Button>
        }
      />
      <KpiContent />
    </div>
  )
}
